import {
  productCategorySchema,
  productCategoryTreeResponseSchema,
  type ProductCategory,
  type ProductCategoryCreateValues,
  type ProductCategoryTreeNode,
  type ProductCategoryUpdateValues,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, apiSend, type ApiError } from '@/lib/api-client';

export const productCategoryKeys = { all: ['product-categories'] as const };

export function useProductCategories(
  input: { q?: string | undefined; includeInactive?: boolean | undefined } = {},
): UseQueryResult<ProductCategoryTreeNode[], ApiError> {
  const search = new URLSearchParams();
  if (input.q !== undefined) search.set('q', input.q);
  if (input.includeInactive) search.set('includeInactive', 'true');
  return useQuery({
    queryKey: [...productCategoryKeys.all, input],
    queryFn: async () =>
      (
        await apiFetch(
          `/api/v1/product-categories?${search.toString()}`,
          productCategoryTreeResponseSchema,
        )
      ).data,
  });
}

function useInvalidateCategories(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: ['product-catalog'] });
  };
}

export function useCreateProductCategory(): UseMutationResult<
  ProductCategory,
  ApiError,
  ProductCategoryCreateValues
> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (input) =>
      apiFetch('/api/v1/product-categories', productCategorySchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateProductCategory(): UseMutationResult<
  ProductCategory,
  ApiError,
  { id: string; input: ProductCategoryUpdateValues }
> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: ({ id, input }) =>
      apiFetch(`/api/v1/product-categories/${id}`, productCategorySchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

export function useArchiveProductCategory(): UseMutationResult<void, ApiError, string> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (id) => apiSend(`/api/v1/product-categories/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
